import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link } from 'react-router-dom';
import { addProvinceLabels, addDistrictLabels } from '../utils/turkeyMapProvinceLabels';
import { normalizeLabelOverride } from '../utils/turkeyMapLabelPositions';
import {
  fetchMapLabelState,
  saveMapLabelState,
  clearMapLabelState,
  EMPTY_MAP_LABEL_STATE,
} from '../services/mapLabelsService';
import {
  fetchAdm2Markup,
  createDistrictSliceSvg,
  fitDistrictSliceViewBox,
  platePrefixFromIso2,
} from '../utils/turkeyMapAdm2Slice';
import { MapZoomableFrame } from '../components/MapZoomableFrame';
import './TechServicesMapPage.css';
import './TechProvinceLabelsEditorPage.css';
import { useConfirmDialog } from '../contexts/ConfirmContext';

const MAP_SRC = `${process.env.PUBLIC_URL || ''}/maps/tr-adm1.svg`;

/** @param {'province' | 'district'} scope */
function attachEditableLabelDrag(svg, scope, onCommit) {
  let active = null;

  const clientToSvg = (cx, cy) => {
    if (typeof svg.createSVGPoint !== 'function') return null;
    const pt = svg.createSVGPoint();
    pt.x = cx;
    pt.y = cy;
    const m = svg.getScreenCTM();
    if (!m) return null;
    return pt.matrixTransform(m.inverse());
  };

  const finishDrag = (textEl, pointerId) => {
    if (!active || active.text !== textEl) return;
    const { id, text: tx, startX, startY } = active;
    tx.classList.remove('province-label--dragging');
    try {
      if (pointerId != null) {
        tx.releasePointerCapture(pointerId);
      }
    } catch {
      /* */
    }
    active = null;
    const x = parseFloat(tx.getAttribute('x'));
    const y = parseFloat(tx.getAttribute('y'));
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    const moved =
      !Number.isFinite(startX) ||
      !Number.isFinite(startY) ||
      Math.abs(x - startX) > 0.25 ||
      Math.abs(y - startY) > 0.25;
    if (moved) onCommit(id, x, y);
  };

  const onMove = (e) => {
    if (!active || e.pointerId !== active.pointerId) return;
    const p = clientToSvg(e.clientX, e.clientY);
    if (!p) return;
    const r = parseFloat(active.text.getAttribute('data-editor-rotate')) || 0;
    active.text.setAttribute('x', String(p.x));
    active.text.setAttribute('y', String(p.y));
    if (r !== 0 && Number.isFinite(r)) {
      active.text.setAttribute('transform', `rotate(${r} ${p.x} ${p.y})`);
    } else {
      active.text.removeAttribute('transform');
    }
  };

  const onUp = (e) => {
    if (!active || e.pointerId !== active.pointerId) return;
    active.text.removeEventListener('pointermove', onMove);
    active.text.removeEventListener('pointerup', onUp);
    active.text.removeEventListener('pointercancel', onUp);
    finishDrag(active.text, e.pointerId);
  };

  const onDown = (e) => {
    if (e.button !== 0 && e.pointerType !== 'touch') return;
    const t = e.target;
    if (t?.tagName !== 'text' || !t.classList?.contains('province-label--draggable')) return;
    const id =
      scope === 'province' ? t.getAttribute('data-province-id') : t.getAttribute('data-district-id');
    if (!id) return;
    e.stopPropagation();
    const startX = parseFloat(t.getAttribute('x'));
    const startY = parseFloat(t.getAttribute('y'));
    active = { text: t, id, startX, startY, pointerId: e.pointerId };
    t.classList.add('province-label--dragging');
    try {
      t.setPointerCapture(e.pointerId);
    } catch {
      /* */
    }
    t.addEventListener('pointermove', onMove);
    t.addEventListener('pointerup', onUp);
    t.addEventListener('pointercancel', onUp);
  };

  svg.addEventListener('pointerdown', onDown);

  return () => {
    if (active) {
      const { text: tx, pointerId } = active;
      tx.removeEventListener('pointermove', onMove);
      tx.removeEventListener('pointerup', onUp);
      tx.removeEventListener('pointercancel', onUp);
      finishDrag(tx, pointerId);
    }
    svg.removeEventListener('pointerdown', onDown);
  };
}

const TechProvinceLabelsEditorPage = () => {
  const { confirm } = useConfirmDialog();
  const countryMountRef = useRef(null);
  const districtMountRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [provinceOptions, setProvinceOptions] = useState([]);

  const [editorMode, setEditorMode] = useState('provinces');
  const [labelState, setLabelState] = useState(EMPTY_MAP_LABEL_STATE);
  const [labelStateLoading, setLabelStateLoading] = useState(true);
  const [labelStateError, setLabelStateError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState(null);

  const [activeProvince, setActiveProvince] = useState(null);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [districtError, setDistrictError] = useState('');

  const commitDragProvince = useCallback((id, x, y) => {
    setLabelState((prev) => {
      const cur = prev.provinces[id] ? { ...prev.provinces[id] } : { x, y, scale: 1, rotate: 0 };
      return {
        ...prev,
        provinces: { ...prev.provinces, [id]: { ...cur, x, y } },
      };
    });
    setDirty(true);
  }, []);

  const commitDragDistrict = useCallback((id, x, y) => {
    setLabelState((prev) => {
      const cur = prev.districts[id] ? { ...prev.districts[id] } : { x, y, scale: 1, rotate: 0 };
      return {
        ...prev,
        districts: { ...prev.districts, [id]: { ...cur, x, y } },
      };
    });
    setDirty(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLabelStateLoading(true);
      setLabelStateError('');
      try {
        const state = await fetchMapLabelState();
        if (!cancelled) {
          setLabelState(state);
          setDirty(false);
        }
      } catch (e) {
        if (!cancelled) {
          setLabelStateError(e?.message || 'Etiket ayarları yüklenemedi');
        }
      } finally {
        if (!cancelled) setLabelStateLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (editorMode === 'provinces' && selected?.scope === 'district') setSelected(null);
    if (editorMode === 'districts' && selected?.scope === 'province') setSelected(null);
  }, [editorMode, selected?.scope]);

  useEffect(() => {
    const el = countryMountRef.current;
    if (!el) return undefined;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(MAP_SRC);
        if (!res.ok) throw new Error(`Harita yüklenemedi (${res.status})`);
        const text = await res.text();
        if (cancelled) return;

        try {
          const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
          const opts = Array.from(doc.querySelectorAll('#features > g[id][iso2]'))
            .map((g) => ({
              id: g.getAttribute('id'),
              title: g.getAttribute('title') || '',
              iso2: g.getAttribute('iso2'),
            }))
            .filter((p) => p.id && p.iso2)
            .sort((a, b) => a.title.localeCompare(b.title, 'tr'));
          setProvinceOptions(opts);
        } catch {
          setProvinceOptions([]);
        }

        el.innerHTML = text;
        const svg = el.querySelector('svg');
        if (svg) {
          svg.removeAttribute('style');
          svg.style.background = 'transparent';
          svg.style.display = 'block';
          svg.style.width = '100%';
          svg.style.height = 'auto';
          svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          svg.setAttribute('role', 'application');
          svg.setAttribute('aria-label', 'İl adlarını sürükleyerek konumlandırın');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Harita dosyası okunamadı');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      el.innerHTML = '';
    };
  }, []);

  useEffect(() => {
    if (editorMode !== 'districts' || !activeProvince?.iso2) return undefined;

    const el = districtMountRef.current;
    if (!el) return undefined;

    let cancelled = false;

    const load = async () => {
      flushSync(() => {
        setDistrictLoading(true);
      });
      setDistrictError('');
      try {
        const plate = platePrefixFromIso2(activeProvince.iso2);
        if (!plate) throw new Error('Bu il için plaka kodu okunamadı.');
        const markup = await fetchAdm2Markup();
        if (cancelled) return;
        const svg = createDistrictSliceSvg(markup, plate);
        if (!svg) throw new Error('Bu il için ilçe haritası bulunamadı.');
        el.innerHTML = '';
        el.appendChild(svg);
        svg.removeAttribute('style');
        svg.style.background = 'transparent';
        svg.style.display = 'block';
        svg.style.width = '100%';
        svg.style.height = 'auto';
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        svg.setAttribute('role', 'application');
        svg.setAttribute('aria-label', `${activeProvince.title} ilçe adlarını düzenleyin`);
      } catch (e) {
        if (!cancelled) {
          setDistrictError(e?.message || 'İlçe haritası yüklenemedi');
        }
      } finally {
        if (!cancelled) setDistrictLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      el.innerHTML = '';
    };
  }, [editorMode, activeProvince?.id, activeProvince?.iso2, activeProvince?.title]);

  useEffect(() => {
    if (loading || error || labelStateLoading || editorMode !== 'provinces' || !countryMountRef.current) {
      return undefined;
    }

    const mount = countryMountRef.current;
    let cancelled = false;
    let removeDrag = () => {};
    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (cancelled) return;
        const svg = mount.querySelector('svg');
        if (!svg) return;
        addProvinceLabels(svg, {
          positionOverrides: labelState.provinces,
          draggable: true,
          editorSelectedId: selected?.scope === 'province' ? selected.id : null,
        });
        removeDrag = attachEditableLabelDrag(svg, 'province', commitDragProvince);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      removeDrag();
    };
  }, [loading, error, labelStateLoading, editorMode, labelState.provinces, selected, commitDragProvince]);

  useEffect(() => {
    if (
      editorMode !== 'districts' ||
      districtLoading ||
      districtError ||
      labelStateLoading ||
      !districtMountRef.current
    ) {
      return undefined;
    }

    const mount = districtMountRef.current;
    let cancelled = false;
    let removeDrag = () => {};
    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (cancelled) return;
        const svg = mount.querySelector('svg');
        if (!svg) return;
        addDistrictLabels(svg, {
          positionOverrides: labelState.districts,
          draggable: true,
          editorSelectedId: selected?.scope === 'district' ? selected.id : null,
          globalFontScale: labelState.districtGlobalFontScale ?? 1,
        });
        requestAnimationFrame(() => {
          fitDistrictSliceViewBox(svg);
        });
        removeDrag = attachEditableLabelDrag(svg, 'district', commitDragDistrict);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      removeDrag();
    };
  }, [
    editorMode,
    districtLoading,
    districtError,
    labelStateLoading,
    activeProvince?.id,
    labelState.districts,
    labelState.districtGlobalFontScale,
    selected,
    commitDragDistrict,
  ]);

  useEffect(() => {
    const mount =
      editorMode === 'provinces' ? countryMountRef.current : districtMountRef.current;
    if (!mount || loading) return undefined;

    const onClick = (e) => {
      const t = e.target;
      if (t?.tagName !== 'text' || !t.classList?.contains('province-label--draggable')) {
        setSelected(null);
        return;
      }
      const pid = t.getAttribute('data-province-id');
      const did = t.getAttribute('data-district-id');
      if (pid && editorMode === 'provinces') {
        setSelected({ scope: 'province', id: pid });
        return;
      }
      if (did && editorMode === 'districts') {
        setSelected({ scope: 'district', id: did });
      }
    };

    mount.addEventListener('click', onClick);
    return () => mount.removeEventListener('click', onClick);
  }, [editorMode, loading, districtLoading, districtError]);

  const readDomCoords = useCallback(
    (scope, id) => {
      const rootEl = scope === 'province' ? countryMountRef.current : districtMountRef.current;
      const attr = scope === 'province' ? 'data-province-id' : 'data-district-id';
      const el = rootEl?.querySelector?.(`text[${attr}="${CSS.escape(id)}"]`);
      const x = parseFloat(el?.getAttribute('x') || '');
      const y = parseFloat(el?.getAttribute('y') || '');
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      return { x, y };
    },
    []
  );

  const patchSelected = useCallback(
    (patch) => {
      if (!selected) return;
      const mapKey = selected.scope === 'province' ? 'provinces' : 'districts';
      setLabelState((prev) => {
        const id = selected.id;
        let cur = prev[mapKey][id];
        if (!cur) {
          const xy = readDomCoords(selected.scope, id);
          if (!xy) return prev;
          cur = { ...xy, scale: 1, rotate: 0 };
        }
        const next = normalizeLabelOverride({ ...cur, ...patch });
        if (!next) return prev;
        return { ...prev, [mapKey]: { ...prev[mapKey], [id]: next } };
      });
      setDirty(true);
    },
    [readDomCoords, selected]
  );

  const clearSelectedOverride = useCallback(() => {
    if (!selected) return;
    const mapKey = selected.scope === 'province' ? 'provinces' : 'districts';
    setLabelState((prev) => {
      const nextMap = { ...prev[mapKey] };
      delete nextMap[selected.id];
      return { ...prev, [mapKey]: nextMap };
    });
    setSelected(null);
    setDirty(true);
  }, [selected]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveMapLabelState(labelState);
      setDirty(false);
      if (window.showNotification) {
        window.showNotification(
          'success',
          'Etiket ayarları kaydedildi. Tüm kullanıcılar Teknik Servisler haritasında aynı düzeni görür.'
        );
      }
    } catch (e) {
      if (window.showNotification) {
        window.showNotification('error', e?.message || 'Etiket ayarları kaydedilemedi.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const ok = await confirm({
      title: 'Etiketleri sıfırla',
      message:
        'Tüm özel il ve ilçe ayarları sunucudan silinecek; konum, boyut ve dönüş varsayılana döner. Emin misiniz?',
      variant: 'danger',
      confirmLabel: 'Evet, sıfırla',
    });
    if (!ok) {
      return;
    }
    setSaving(true);
    try {
      await clearMapLabelState();
      setLabelState(EMPTY_MAP_LABEL_STATE);
      setSelected(null);
      setDirty(false);
      if (window.showNotification) {
        window.showNotification('success', 'Kayıtlar temizlendi.');
      }
    } catch (e) {
      if (window.showNotification) {
        window.showNotification('error', e?.message || 'Etiket ayarları sıfırlanamadı.');
      }
    } finally {
      setSaving(false);
    }
  };

  const adjustedProvince = Object.keys(labelState.provinces).length;
  const adjustedDistrict = Object.keys(labelState.districts).length;

  const selectedEntry =
    selected && labelState[selected.scope === 'province' ? 'provinces' : 'districts'][selected.id]
      ? labelState[selected.scope === 'province' ? 'provinces' : 'districts'][selected.id]
      : null;

  const inspectorScale = selectedEntry?.scale ?? 1;
  const inspectorRotate = selectedEntry?.rotate ?? 0;
  const districtGlobalFontScale = labelState.districtGlobalFontScale ?? 1;

  const selectedTitle =
    selected?.scope === 'province'
      ? provinceOptions.find((p) => p.id === selected.id)?.title || selected.id
      : selected?.id?.replace(/^\d+-/, '').replace(/-/g, ' ') || '';

  return (
    <main className="main-content tech-services-map-page tech-province-labels-editor">
      <header className="tech-services-map-page__header tech-province-labels-editor__header">
        <div>
          <h1>Harita etiket konumları</h1>
          <p className="tech-services-map-page__lead">
            İl ve ilçe adlarını sürükleyin; bir etikete tıklayarak <strong>boyut</strong> ve{' '}
            <strong>dönüş</strong> verin. <strong>Kaydet</strong> sunucuya yazar; tüm tarayıcılar aynı
            düzeni görür.
          </p>
        </div>
        <div className="tech-province-labels-editor__actions">
          {dirty && (
            <span className="tech-province-labels-editor__dirty" role="status">
              Kaydedilmemiş değişiklik
            </span>
          )}
          <Link
            to="/teknik-servisler"
            className="tech-province-labels-editor__btn tech-province-labels-editor__btn--ghost"
          >
            <i className="fas fa-arrow-left" aria-hidden="true" /> Haritaya dön
          </Link>
          <button
            type="button"
            className="tech-province-labels-editor__btn tech-province-labels-editor__btn--danger"
            onClick={handleReset}
            disabled={saving || labelStateLoading}
          >
            Varsayılana sıfırla
          </button>
          <button
            type="button"
            className="tech-province-labels-editor__btn tech-province-labels-editor__btn--primary"
            onClick={handleSave}
            disabled={saving || labelStateLoading || !dirty}
          >
            <i className="fas fa-save" aria-hidden="true" /> {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </header>

      {labelStateError && (
        <div className="tech-services-map-page__error tech-province-labels-editor__state-error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden="true" />
          <span>{labelStateError}</span>
        </div>
      )}

      <div className="tech-province-labels-editor__tabs" role="tablist" aria-label="Düzenleme kapsamı">
        <button
          type="button"
          role="tab"
          aria-selected={editorMode === 'provinces'}
          className={`tech-province-labels-editor__tab ${editorMode === 'provinces' ? 'is-active' : ''}`}
          onClick={() => setEditorMode('provinces')}
        >
          İl etiketleri
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={editorMode === 'districts'}
          className={`tech-province-labels-editor__tab ${editorMode === 'districts' ? 'is-active' : ''}`}
          onClick={() => setEditorMode('districts')}
        >
          İlçe etiketleri
        </button>
      </div>

      {editorMode === 'districts' && (
        <>
          <div className="tech-province-labels-editor__province-pick">
            <label htmlFor="editor-province-select" className="tech-province-labels-editor__pick-label">
              İl
            </label>
            <select
              id="editor-province-select"
              className="tech-province-labels-editor__select"
              value={activeProvince?.id || ''}
              onChange={(e) => {
                const id = e.target.value;
                const row = provinceOptions.find((p) => p.id === id);
                if (row) {
                  setActiveProvince({ id: row.id, title: row.title, iso2: row.iso2 });
                } else {
                  setActiveProvince(null);
                }
                setDistrictError('');
                setSelected(null);
              }}
            >
              <option value="">— İl seçin —</option>
              {provinceOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <div className="tech-province-labels-editor__global-district-font" aria-label="Tüm ilçe etiket yazı boyutu">
            <div className="tech-province-labels-editor__global-district-font-head">
              <label htmlFor="district-global-font-scale">Tüm ilçe yazıları — genel boyut</label>
              <span className="tech-province-labels-editor__value">{districtGlobalFontScale.toFixed(2)}×</span>
            </div>
            <p className="tech-province-labels-editor__global-district-font-hint">
              Açık il haritasındaki bütün ilçe adları aynı anda büyür / küçülür. Tekil etiket boyutu ile çarpılır.
            </p>
            <div className="tech-province-labels-editor__slider-row">
              <input
                id="district-global-font-scale"
                type="range"
                min={0.25}
                max={3}
                step={0.05}
                value={districtGlobalFontScale}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setLabelState((prev) => ({ ...prev, districtGlobalFontScale: v }));
                  setDirty(true);
                }}
              />
            </div>
          </div>
        </>
      )}

      <div className="tech-province-labels-editor__workspace">
        <section
          className="tech-services-map-page__card tech-province-labels-editor__card"
          aria-busy={editorMode === 'provinces' ? loading || labelStateLoading : districtLoading || labelStateLoading}
        >
          {labelStateLoading && (
            <div className="tech-services-map-page__loading">
              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
              <span>Etiket ayarları yükleniyor…</span>
            </div>
          )}
          {editorMode === 'provinces' && loading && (
            <div className="tech-services-map-page__loading">
              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
              <span>Harita yükleniyor…</span>
            </div>
          )}
          {editorMode === 'provinces' && error && (
            <div className="tech-services-map-page__error" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
          {editorMode === 'districts' && districtLoading && (
            <div className="tech-services-map-page__loading">
              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
              <span>İlçe haritası yükleniyor…</span>
            </div>
          )}
          {editorMode === 'districts' && districtError && (
            <div className="tech-services-map-page__error" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              <span>{districtError}</span>
            </div>
          )}

          {editorMode === 'provinces' && (
            <MapZoomableFrame
              resetKey="ed-provinces"
              className="tech-province-labels-editor__zoom"
              hideToolbar={loading || !!error || labelStateLoading}
            >
              <div
                ref={countryMountRef}
                className="tech-services-map-page__svg-root turkey-map-svg-root tech-province-labels-editor__map"
                hidden={!!error || loading || labelStateLoading}
              />
            </MapZoomableFrame>
          )}
          {editorMode === 'districts' && activeProvince && (
            <MapZoomableFrame
              resetKey={`ed-districts-${activeProvince.id}`}
              className="tech-province-labels-editor__zoom"
              hideToolbar={districtLoading || !!districtError || labelStateLoading}
            >
              <div
                ref={districtMountRef}
                className="tech-services-map-page__svg-root turkey-map-svg-root tech-province-labels-editor__map"
                hidden={!!districtError || districtLoading || labelStateLoading}
              />
            </MapZoomableFrame>
          )}
          {editorMode === 'districts' && !activeProvince && !districtLoading && (
            <p className="tech-province-labels-editor__hint">İlçe haritasını görmek için yukarıdan bir il seçin.</p>
          )}
        </section>

        <aside className="tech-province-labels-editor__inspector" aria-label="Seçili etiket">
          <h2 className="tech-province-labels-editor__inspector-title">Seçili etiket</h2>
          {!selected && (
            <p className="tech-province-labels-editor__inspector-empty">
              Bir etikete tıklayın; ardından boyut ve açıyı buradan ayarlayın.
            </p>
          )}
          {selected && (
            <>
              <p className="tech-province-labels-editor__inspector-meta">
                <span className="tech-province-labels-editor__inspector-scope">
                  {selected.scope === 'province' ? 'İl' : 'İlçe'}
                </span>
                <strong>{selectedTitle}</strong>
              </p>
              <div className="tech-province-labels-editor__field">
                <label htmlFor="label-scale">Boyut (ölçek)</label>
                {selected.scope === 'district' && (
                  <p className="tech-province-labels-editor__field-hint">
                    Etkili çarpan: {(districtGlobalFontScale * inspectorScale).toFixed(2)}× (genel × etiket)
                  </p>
                )}
                <div className="tech-province-labels-editor__slider-row">
                  <input
                    id="label-scale"
                    type="range"
                    min={0.25}
                    max={3}
                    step={0.05}
                    value={inspectorScale}
                    onChange={(e) => patchSelected({ scale: parseFloat(e.target.value) })}
                  />
                  <span className="tech-province-labels-editor__value">{inspectorScale.toFixed(2)}×</span>
                </div>
              </div>
              <div className="tech-province-labels-editor__field">
                <label htmlFor="label-rotate">Dönüş (derece)</label>
                <div className="tech-province-labels-editor__slider-row">
                  <input
                    id="label-rotate"
                    type="range"
                    min={-180}
                    max={180}
                    step={1}
                    value={inspectorRotate}
                    onChange={(e) => patchSelected({ rotate: parseFloat(e.target.value) })}
                  />
                  <span className="tech-province-labels-editor__value">{Math.round(inspectorRotate)}°</span>
                </div>
              </div>
              <button
                type="button"
                className="tech-province-labels-editor__btn tech-province-labels-editor__btn--ghost tech-province-labels-editor__btn--block"
                onClick={clearSelectedOverride}
              >
                Bu etiketi varsayılana sıfırla
              </button>
            </>
          )}
        </aside>
      </div>

      <p className="tech-province-labels-editor__meta">
        Özelleştirilmiş il: <strong>{adjustedProvince}</strong> · ilçe: <strong>{adjustedDistrict}</strong>
      </p>
    </main>
  );
};

export default TechProvinceLabelsEditorPage;
